import type { FC } from 'react';
import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  KeyboardArrowDownIcon as ArrowDownIcon,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  LogoutIcon,
  Paper,
  Popper,
  useTheme,
} from '@/shared/ui/mui';
import type { User } from '@/shared/types';
import type { MenuItem } from '../../side-menu/types';

interface DesktopProfileMenuProps {
  user: User | null;
  menuItems: MenuItem[];
  commonItems: MenuItem[];
  isHomePage: boolean;
  isScrolled: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  logout: () => void;
}

export const DesktopProfileMenu: FC<DesktopProfileMenuProps> = ({
  user,
  menuItems,
  commonItems,
  isHomePage,
  isScrolled,
  isOpen,
  onToggle,
  onClose,
  logout,
}) => {
  const theme = useTheme();
  const profileAnchorRef = useRef<HTMLButtonElement>(null);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      profileAnchorRef.current &&
      profileAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    onClose();
  };

  const menuItemHoverSx = {
    py: 1,
    borderRadius: 1,
    mx: 1,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '& .MuiListItemIcon-root': { color: 'white' },
      '& .MuiListItemText-primary': { color: 'white' },
    },
  };

  return (
    <div>
      <Button
        ref={profileAnchorRef}
        onClick={onToggle}
        endIcon={<ArrowDownIcon />}
        startIcon={
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.9rem',
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() ||
              user?.email?.charAt(0)?.toUpperCase() ||
              'U'}
          </Avatar>
        }
        sx={{
          color:
            isHomePage && !isScrolled
              ? theme.palette.common.white
              : theme.palette.primary.main,
          textTransform: 'none',
          ml: 2,
        }}
      >
        {user?.name || user?.email || 'Menu'}
      </Button>
      <Popper
        open={isOpen}
        anchorEl={profileAnchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'top right' }}
          >
            <Paper
              elevation={8}
              sx={{
                mt: 1,
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <List sx={{ width: 300, py: 0.5, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                  {Array.isArray(menuItems) &&
                    menuItems.map((item, index) => {
                      const showTopLevelDivider = index > 0;

                      if (item.subItems && item.subItems.length > 0) {
                        return (
                          <React.Fragment key={item.text}>
                            {showTopLevelDivider && <Divider sx={{ my: 0.5 }} />}
                            <ListSubheader
                              sx={{
                                backgroundColor: 'transparent',
                                color: theme.palette.primary.main,
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                py: 1.5,
                                letterSpacing: '0.05em',
                              }}
                            >
                              {item.text}
                            </ListSubheader>
                            {item.subItems.map((subItem) => {
                              const SubItemIcon = subItem.icon;
                              return (
                                <ListItemButton
                                  key={subItem.text}
                                  component={RouterLink}
                                  to={subItem.path || '#'}
                                  onClick={handleClose}
                                  sx={{ ...menuItemHoverSx, pl: 3 }}
                                >
                                  {SubItemIcon && (
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <SubItemIcon fontSize="small" />
                                    </ListItemIcon>
                                  )}
                                  <ListItemText
                                    primary={subItem.text}
                                    slotProps={{ primary: { fontSize: '1rem', fontWeight: 500 } }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </React.Fragment>
                        );
                      }

                      const ItemIcon = item.icon;
                      return item.path ? (
                        <React.Fragment key={item.text}>
                          {showTopLevelDivider && <Divider sx={{ my: 0.5 }} />}
                          <ListItemButton
                            component={RouterLink}
                            to={item.path}
                            onClick={handleClose}
                            sx={menuItemHoverSx}
                          >
                            {ItemIcon && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <ItemIcon fontSize="small" />
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={item.text}
                              slotProps={{ primary: { fontSize: '1rem', fontWeight: 500 } }}
                            />
                          </ListItemButton>
                        </React.Fragment>
                      ) : null;
                    })}

                  {Array.isArray(commonItems) && commonItems.length > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      {commonItems.map((item) => {
                        const CommonItemIcon = item.icon;
                        return (
                          <ListItemButton
                            key={item.text}
                            component={RouterLink}
                            to={item.path || '#'}
                            onClick={handleClose}
                            sx={menuItemHoverSx}
                          >
                            {CommonItemIcon && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CommonItemIcon fontSize="small" />
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={item.text}
                              slotProps={{ primary: { fontSize: '1rem', fontWeight: 500 } }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </>
                  )}

                  <Divider sx={{ my: 0.5 }} />
                  <ListItemButton
                    onClick={() => {
                      onClose();
                      logout();
                    }}
                    sx={{
                      py: 1,
                      mx: 1,
                      borderRadius: 1,
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.common.white,
                        '& .MuiListItemIcon-root': { color: theme.palette.common.white },
                        '& .MuiListItemText-primary': { color: theme.palette.common.white },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Logout"
                      slotProps={{ primary: { fontSize: '1rem', fontWeight: 600 } }}
                    />
                  </ListItemButton>
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
